import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Layers, Package, Plus, QrCode } from 'lucide-react';
import { allocationsApi, stockApi } from '@/features/stock/api';
import { eventsApi } from '@/features/events/api';
import { reporterAdminApi } from '@/features/reporter/adminApi';
import { queryKeys } from '@/lib/api/queryKeys';
import { projectStockPoolApi, type ProjectPoolRow } from './projectStockPoolApi';
import { useFormPanel, type FormPanelKind } from '@/components/layout/formPanelStore';
import { useRightSidebar } from '@/components/layout/RightSidebar';
import type { XkEvent } from '@/lib/api/types';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { eventPhase, isPlannable } from '../eventPhase';
import type { DragData, DropData } from './dnd';
import { XotskiCarousel } from './XotskiCarousel';
import { StationSource } from './StationSource';
import { ReporterAvatar } from './ReporterAvatar';
import { EditableEventList } from './EditableEventList';
import { BatchCreateEventsDialog } from './BatchCreateEventsDialog';

/**
 * PLAN tab — the Planner. Drag xotskis / reporters / stations from the
 * sources onto an event to attach them; click an event to edit its
 * properties; batch-create events. Owns the single <DndContext>.
 */
export function PlannerTab({
  groupId,
  brandId,
  companyId,
  projectStart,
  projectEnd,
  events,
  totalEventCount,
  focusEventId,
  onLimitReached,
  singleEvent = false,
  leftFooter,
}: {
  groupId: number;
  brandId: number;
  companyId?: number;
  projectStart: string | null | undefined;
  projectEnd: string | null | undefined;
  events: XkEvent[];
  totalEventCount: number;
  focusEventId?: number | null;
  onLimitReached: () => void;
  /**
   * When embedded on the Event detail page we plan ONE event: skip the
   * upcoming/edit phase filter (so a live/done event still shows) and hide the
   * batch-create affordance.
   */
  singleEvent?: boolean;
  /** Optional content rendered at the bottom of the LEFT column (under the
   * event list) — used by the Event page to align the Shared-QR manager with
   * the event block instead of stretching it full-width. */
  leftFooter?: React.ReactNode;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const openForm = useFormPanel((st) => st.openForm);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(focusEventId ?? null);

  /*
   * Lo que el planner puso en el carril se va con el planner.
   *
   * El store del carril es global y no se entera de que has cambiado de
   * página: las propiedades del evento elegido en el tablero seguían puestas
   * (plegadas) en la pantalla siguiente, con su manija y sin nada que ver con
   * ella. Sólo se cierra lo que es del planner; un formulario abierto desde
   * otro sitio no es asunto suyo.
   */
  useEffect(
    () => () => {
      const propios: FormPanelKind[] = [
        'event-properties',
        'event-summary',
        'station-allocations',
        'station-preview',
      ];
      const activo = useFormPanel.getState().active;
      if (activo && propios.includes(activo.kind)) {
        useFormPanel.getState().closeForm({ collapse: true });
      }
    },
    [],
  );
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const plannable = useMemo(
    () => (singleEvent ? events : events.filter((e) => isPlannable(e))),
    [events, singleEvent],
  );

  /**
   * Selecting an event puts its properties and reporters in the right rail.
   * The rail is the single in-context editor: the eye icon shows the game
   * preview, a station's sliders show its allocations, and this shows the
   * event — whichever was asked for last.
   */
  const selectEvent = (id: number) => {
    setSelectedEventId(id);
    // En un evento EN CURSO seleccionar no abre las propiedades por su cuenta:
    // durante el evento el carril es para el monitor, y quien quiera editar
    // tiene el botón «Propiedades» en la cabecera de la fila.
    const ev = events.find((e) => e.id === id);
    if (ev && eventPhase(ev) === 'live') return;
    openEventProperties(id);
  };

  /*
   * Con qué estado se monta el carril de propiedades (23 sep 2026):
   *   · evento ya pasado → plegado por defecto; sus propiedades están a un clic
   *     (manija o botón del TopBar), pero no se comen la pantalla;
   *   · evento sin pasar en modo plan → abierto por defecto;
   *   · al elegir a mano un evento pasado con el carril ya abierto, se
   *     respeta: plegárselo a alguien que lo acaba de abrir sería pelearse.
   */
  const yaPaso = (ev: XkEvent | undefined) => !!ev && eventPhase(ev) === 'done';
  const openEventProperties = (id: number, opts: { auto?: boolean } = {}) => {
    const ev = events.find((e) => e.id === id);
    const abierto = opts.auto ? !yaPaso(ev) : !yaPaso(ev) || useRightSidebar.getState().open;
    openForm({
      kind: 'event-properties',
      mode: 'edit',
      // The rail header carries the event's name; the panel inside already
      // says "Event properties", so repeating it would just stutter.
      title: ev?.name ?? t('planner.properties.title'),
      context: { eventId: id, groupId, brandId, projectStart, projectEnd },
      initiallyOpen: abierto,
    });
  };

  /*
   * En modo plan el carril de propiedades sale solo, con el evento enfocado o
   * el primero que quede por planificar: quien entra a planificar viene a
   * tocar el evento, no a buscar dónde se toca. Una vez por montaje, y nunca
   * para un evento en curso —durante el evento el carril es del monitor—.
   */
  const autoAbierto = useRef(false);
  useEffect(() => {
    if (autoAbierto.current || events.length === 0) return;
    const objetivo =
      (focusEventId != null ? events.find((e) => e.id === focusEventId) : undefined) ??
      plannable[0] ??
      events[0];
    if (!objetivo) return;
    autoAbierto.current = true;
    if (eventPhase(objetivo) === 'live') return;
    setSelectedEventId(objetivo.id);
    openEventProperties(objetivo.id, { auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una vez por montaje, cuando llegan los eventos
  }, [events, focusEventId, plannable]);

  const onDragStart = (e: DragStartEvent) => {
    setActiveDrag((e.active.data.current as DragData) ?? null);
  };

  /**
   * Decision A (task 3.4) — spread one xotski's entire free pool across every
   * station of an event, evenly, remainder to the first stations.
   *
   * "Free pool" is the API's own `stock.headroom` — the number its create
   * gate applies — capped by the project pool when adopted. Nothing is
   * re-derived here: the old brand-wide sum was paginated to 25 rows and
   * counted `quantity` where the gate counts `available`, so it built splits
   * the API then refused.
   *
   * Stations that already carry this xotski are left untouched rather than
   * topped up — the drop stays predictable, and the numbers are editable in
   * the right rail afterwards.
   */
  const spreadAcrossStations = async (
    eventId: number,
    stockId: number,
    stockName: string,
    /** En el reparto masivo los avisos por premio son ruido: se resume al final. */
    silencioso = false,
  ): Promise<boolean> => {
    const [stationsRes, eventAllocs, stockRes, projectPool] = await Promise.all([
      eventsApi.listStations({ eventId }),
      // Only THIS event's rows, and all of them: they decide which stations
      // already carry the prize. The brand-wide list was capped at 25.
      allocationsApi.list({ eventId }, { perPage: 500 }),
      stockApi.list({ companyId: brandId }, { perPage: 500 }),
      // Task 7.2 — the project's own ceiling, when it has adopted the pool.
      projectStockPoolApi.list(groupId).catch(() => [] as ProjectPoolRow[]),
    ]);

    const stations = stationsRes.items ?? [];
    if (stations.length === 0) {
      if (!silencioso) toast({ tone: 'info', title: t('planner.drop.xotskiNoStations') });
      return false;
    }

    const stock = (stockRes.items ?? []).find((s) => s.id === stockId);
    const brandFree = stock?.headroom;
    if (brandFree == null) {
      // Legacy rows predate the pool migration and are uncapped; without a
      // number there is nothing to divide, so fall back to guidance.
      if (!silencioso) toast({ tone: 'info', title: t('planner.drop.xotskiUseStation') });
      return false;
    }
    // The project pool, once adopted, is the tighter ceiling: spreading past
    // it would hand out units this project was never given.
    const projectRow = projectPool.find((r) => r.stockId === stockId);
    const free = projectRow?.adopted ? Math.min(brandFree, projectRow.remaining) : brandFree;
    if (free === 0) {
      if (!silencioso) toast({ tone: 'info', title: t('planner.drop.xotskiNoStock', { name: stockName }) });
      return false;
    }

    const targets = stations.filter(
      (s) => !(eventAllocs.items ?? []).some(
        (a) => a.stationId === s.id && a.stockId === stockId && a.status === 'Enable',
      ),
    );
    if (targets.length === 0) {
      if (!silencioso)
        toast({ tone: 'info', title: t('planner.drop.xotskiOnStationExists', { name: stockName }) });
      return false;
    }

    // Even split; the first `remainder` stations take one extra unit each.
    const base = Math.floor(free / targets.length);
    const remainder = free % targets.length;
    const shares = targets.map((_, i) => base + (i < remainder ? 1 : 0));
    // With fewer units than stations `base` is 0, so the tail would get
    // nothing — drop those rather than create empty allocations.
    const plan = targets
      .map((s, i) => ({ station: s, qty: shares[i] }))
      .filter((p) => p.qty > 0);

    for (const { station, qty } of plan) {
      await allocationsApi.create({
        eventId,
        stationId: station.id,
        stockId,
        quantity: qty,
        available: qty,
        companyId,
      } as Parameters<typeof allocationsApi.create>[0]);
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byBrand(brandId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(brandId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.projectStockPool.byProject(groupId) });
    for (const { station } of plan) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stockAllocations.byStation(station.id),
      });
    }
    if (!silencioso) {
      toast({
        tone: 'success',
        title: t('planner.drop.xotskiSpread', {
          name: stockName,
          total: String(plan.reduce((s, p) => s + p.qty, 0)),
          count: String(plan.length),
        }),
      });
    }
    return true;
  };

  /**
   * Repartir los premios de la marca en este evento, sin arrastrar nada.
   *
   * El único gesto que existía era el arrastre, y si no engancha —con teclado,
   * con lector de pantalla, o simplemente si no se adivina— no había alternativa
   * a la vista: la estación se quedaba sin premios y el invitado llegaba al
   * juego sin nada que ganar (fricciones 11 y 12 del recorrido de QA).
   *
   * Reparte TODO lo que tiene libre, que es lo que quiere quien monta su primer
   * evento. Quien afine premio a premio sigue teniendo el arrastre y el panel de
   * cada estación.
   */
  const repartirTodoEnEvento = async (eventId: number) => {
    const stockRes = await stockApi.list({ companyId: brandId });
    const items = stockRes.items ?? [];
    if (items.length === 0) {
      toast({ tone: 'info', title: t('planner.spreadAll.noStock') });
      return;
    }
    let repartidos = 0;
    for (const s of items) {
      if (await spreadAcrossStations(eventId, s.id, s.name, true)) repartidos += 1;
    }
    toast({
      tone: repartidos > 0 ? 'success' : 'info',
      title:
        repartidos > 0
          ? t('planner.spreadAll.done', { n: String(repartidos) })
          : t('planner.spreadAll.nothing'),
    });
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveDrag(null);
    const data = e.active.data.current as DragData | undefined;
    const drop = e.over?.data.current as DropData | undefined;
    if (!data || !drop?.eventId) return;
    const eventId = drop.eventId;

    try {
      if (data.type === 'xotski') {
        // Task 3.4 / decision A — dropping on the bare EVENT row auto-spreads
        // the xotski's whole free pool evenly across that event's stations,
        // remainder to the first ones. Dropping on a single station card still
        // adds just that one (below).
        if (!drop.stationId) {
          await spreadAcrossStations(eventId, data.stockId, data.name);
          return;
        }
        const here = await allocationsApi.list({ stationId: drop.stationId });
        if (here.items.some((a) => a.stockId === data.stockId)) {
          toast({ tone: 'info', title: t('planner.drop.xotskiOnStationExists', { name: data.name }) });
          return;
        }
        await allocationsApi.create({
          eventId,
          stationId: drop.stationId,
          stockId: data.stockId,
          quantity: 1,
          available: 1,
          companyId,
        } as Parameters<typeof allocationsApi.create>[0]);
        queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byEvent(eventId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byStation(drop.stationId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byBrand(brandId) });
        // The lane's badge is the API's `stock.headroom`, so the stock list
        // has to refresh too — and the project pool, whose `allocated` moved.
        queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(brandId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.projectStockPool.byProject(groupId) });
        toast({ tone: 'success', title: t('planner.drop.xotskiToStation', { name: data.name }) });
      } else if (data.type === 'reporter') {
        const existing = await reporterAdminApi.listForEvent(eventId);
        if (existing.some((r) => r.email.toLowerCase() === data.email.toLowerCase())) {
          toast({ tone: 'info', title: t('planner.drop.reporterExists', { name: data.name }) });
          return;
        }
        await reporterAdminApi.invite({ eventId, email: data.email, name: data.name, brandId });
        queryClient.invalidateQueries({ queryKey: queryKeys.reporters.byEvent(eventId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.reporters.byBrand(brandId) });
        toast({ tone: 'success', title: t('planner.drop.reporterDone', { name: data.name }) });
      } else if (data.type === 'station') {
        const existing = await eventsApi.listStations({ eventId });
        await eventsApi.createStation({
          eventId,
          name: `Station ${existing.items.length + 1}`,
          companyId,
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
        toast({ tone: 'success', title: t('planner.drop.stationDone') });
      }
    } catch (err) {
      toast({ tone: 'error', title: t('planner.drop.failed'), description: (err as Error).message });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="grid gap-4">
        {/* Left column — the assignment board: a pinned warehouse lane on top,
            every event scrolling beneath it as a drop target. Task 3.3: the
            warehouse must stay on screen while you reach any event, so you are
            never scrolling back and forth between what you have and where it
            goes. */}
        {/* `min-w-0`: sin él, el ítem de grid toma como ancho mínimo el de la
            tira de xotskies (que no se parte), y la página entera se ensancha
            más que el viewport: la TopBar sticky se queda corta y el raíl
            derecho tapa lo que asoma por detrás. Con él, la tira se desplaza
            dentro de su tarjeta. */}
        <div className="min-w-0 space-y-4">
          <div
            data-tour="planner-xotskis"
            className="sticky top-0 z-20 -mx-1 bg-[var(--color-bg-primary)] px-1 pb-2 pt-1"
          >
            <XotskiCarousel brandId={brandId} groupId={groupId} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span data-tour="planner-stations">
              <StationSource />
            </span>
            {!singleEvent && (
              <span data-tour="project-add-events" className="flex items-center gap-2">
                {/*
                  UN evento, de uno en uno — y el lote al lado.
                  Lo único que se ofrecía era «Nuevos eventos» (plural): pedía
                  cuántos y los llamaba Event1, Event2… Quien viene a montar una
                  feria concreta tenía que crear un lote y renombrar después
                  (fricción 8 y 9 del recorrido de QA). El creador por lotes se
                  queda: es lo correcto para una gira de veinte fechas.
                */}
                <Button
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() =>
                    openForm({
                      kind: 'event',
                      mode: 'create',
                      title: t('planner.newEvent'),
                      context: { brandId, projectId: groupId },
                    })
                  }
                >
                  {t('planner.newEvent')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setBatchOpen(true)}>
                  {t('planner.batch.open')}
                </Button>
              </span>
            )}
          </div>
          <div data-tour="planner-events">
            <div className="mb-2 flex items-center gap-2">
              <Layers className="h-4 w-4 text-[var(--color-accent-ink)]" />
              <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
                {t('planner.events.title')}
              </h3>
            </div>
            {/* No inner scroll any more: the warehouse lane above is sticky,
                so the page can scroll normally and every event stays a
                reachable drop target. A nested scroller here used to mean
                dragging from a warehouse you could no longer see. */}
            <div className="pr-1">
              <EditableEventList
                groupId={groupId}
                brandId={brandId}
                events={plannable}
                onRepartirTodo={repartirTodoEnEvento}
                selectedEventId={selectedEventId}
                onSelect={selectEvent}
                onOpenProperties={openEventProperties}
                focusEventId={focusEventId}
                // On the event page the row's "Open config" links to the page
                // you're already on — hide it there.
                hideOpenConfig={singleEvent}
                // Y tampoco se desplaza a él: es el único y es la página.
                autoScrollToFocus={!singleEvent}
              />
            </div>
          </div>
          {leftFooter}
        </div>

        {/* No permanent right column: an event's properties and reporters
            belong to a selection, so they open in the right rail when one is
            made (see `selectEvent`). The board keeps the full width. */}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <div className="flex -rotate-3 cursor-grabbing items-center gap-2 rounded-xl border-2 border-[var(--color-accent-ink)] bg-[var(--color-bg-surface)] px-3 py-2 text-xs font-semibold text-[var(--color-xk-lavender)] shadow-2xl shadow-[var(--color-xk-accent)]/30">
            {activeDrag.type === 'xotski' &&
              (activeDrag.image ? (
                <img src={resolveImageUrl(activeDrag.image)} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-bg-base)]">
                  <Package className="h-4 w-4 text-[var(--color-accent-ink)]" />
                </span>
              ))}
            {activeDrag.type === 'reporter' && (
              <ReporterAvatar name={activeDrag.name} email={activeDrag.email} size={28} />
            )}
            {activeDrag.type === 'station' && (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-bg-base)]">
                <QrCode className="h-4 w-4 text-[var(--color-accent-ink)]" />
              </span>
            )}
            <span className="max-w-[140px] truncate">
              {activeDrag.type === 'station' ? t('planner.stationSource.label') : activeDrag.name}
            </span>
          </div>
        ) : null}
      </DragOverlay>

      {!singleEvent && (
        <BatchCreateEventsDialog
          open={batchOpen}
          onOpenChange={setBatchOpen}
          groupId={groupId}
          companyId={companyId}
          projectStart={projectStart}
          projectEnd={projectEnd}
          existingCount={totalEventCount}
          onLimitReached={onLimitReached}
        />
      )}
    </DndContext>
  );
}
